"use client";
import { useCallback, useEffect, useState } from "react";
import type { EventBinding } from "@/lib/api/events";
import {
  evaluationBindingKey,
  persistEvaluationOperation,
  removeEvaluationOperation,
  restoreEvaluationOperations,
  type EvaluationOperation,
} from "@/lib/commands/evaluation-operation-store";

export function useEvaluationRecovery(binding: EventBinding) {
  const [records, setRecords] = useState<EvaluationOperation[]>([]);
  const [ready, setReady] = useState(false),
    [available, setAvailable] = useState(false);
  const read = useCallback(
    () => restoreEvaluationOperations(window.localStorage, binding),
    [binding],
  );
  const sync = useCallback(() => {
    try {
      setRecords(read());
      setAvailable(true);
    } catch {
      setAvailable(false);
    } finally {
      setReady(true);
    }
  }, [read]);
  useEffect(() => {
    const timer = setTimeout(sync, 0);
    const changed = (event: StorageEvent) => {
      const prefix = evaluationBindingKey(binding);
      if (
        !event.key ||
        event.key.startsWith(prefix) ||
        event.key.startsWith(prefix.replace(".v1:", ".v2:"))
      )
        sync();
    };
    window.addEventListener("storage", changed);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", changed);
    };
  }, [binding, sync]);
  function save(record: EvaluationOperation) {
    try {
      persistEvaluationOperation(window.localStorage, binding, record);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  function remove(key: string) {
    try {
      removeEvaluationOperation(window.localStorage, binding, key);
      sync();
      return true;
    } catch {
      setAvailable(false);
      return false;
    }
  }
  return { records, ready, available, read, sync, save, remove };
}
